<!--
  Editor for "API call" actions
-->

<template>
  <orderable-box
    icon="el-icon-guide"
    v-bind="$attrs"
    v-on="$listeners">
    <template slot="content">
      API call
      <tooltip-icon placement="top">
        Perform an <em>API call</em> and store the response in a <em>local variables </em><code>@variable</code>.
      </tooltip-icon>
    </template>
    <el-form-item
      :prop="prop && prop+'.variable'"
      :required="true"
      :rules="VariableValidator">
      <template slot="label">
        Target variable
        <tooltip-icon placement="top">
          The <em>local variable</em> where to store the response:<br/>
          <code>{<br/>
          &nbsp;&nbsp;code: 200,<br/>
          &nbsp;&nbsp;response: ...,<br/>
          }</code>
        </tooltip-icon>
      </template>
      <el-entity-input style="max-width: 250px"
        v-model="variable"
        placeholder="variable name"
        type="entity"
        :disabled="disabled">
      </el-entity-input>
    </el-form-item>
    <el-form-item
      label="Authenticated API"
      :prop="prop && prop+'.api'"
      :required="true">
      <el-button-group style="display: inline-flex !important;">
        <el-filterable-select
          v-model="api"
          :options="apiOptions"
          value-field="api"
          label-field="name"
          :disabled="disabled">
        </el-filterable-select>
        <router-link v-if="api"
          style="padding: 12px"
          :to="{
            name: 'api',
            params: {skill: $route.params.skill, api: api},
            query: $route.query,
          }"
          target="_blank"
          class="el-button"
          :tabindex="-1">
        </router-link>
        <router-link v-else
          style="padding: 12px"
          :to="{
            name: 'api',
            params: {skill: $route.params.skill, api: 'new'},
            query: $route.query,
          }"
          target="_blank"
          class="el-button"
          :tabindex="-1">
          <i class="el-icon-circle-plus-outline"></i>
        </router-link>
      </el-button-group>
    </el-form-item>
    <el-form-item
      label="Call URL"
      :prop="prop && prop+'.url'"
      :rules="urlRules"
      :required="true">
      <el-button-group>
        <el-select style="max-width: 100px"
          v-model="method"
          :disabled="disabled">
          <el-option v-for="item in METHOD_OPTIONS"
            :key="item"
            :value="item">
          </el-option>
        </el-select>
        <el-string-autocomplete
          v-model="url"
          :required="required"
          :disabled="disabled"
          :local-variables="localVariables"
          :context-variables="contextVariables">
        </el-string-autocomplete>
      </el-button-group>
    </el-form-item>
    <el-form-item
      label="Call body"
      :prop="prop && prop+'.body_type'">
      <el-select style="max-width: 150px"
        v-model="body_type"
        :disabled="disabled">
        <el-option v-for="item in BODY_OPTIONS"
          :key="item.value"
          :value="item.value">
          {{ item.label }}
          <tooltip-icon v-if="item.tooltip" placement="right">
            <span v-html="item.tooltip"></span>
          </tooltip-icon>
        </el-option>
      </el-select>
    </el-form-item>
    <el-form-small-item v-if="body_type === 'string'"
      :prop="prop && prop+'.body'"
      :rules="StringVarValidator">
      <el-string-autocomplete
        multiline
        placeholder="Please input"
        v-model="string_body"
        :disabled="disabled"
        :local-variables="localVariables"
        :context-variables="contextVariables"
        :autosize="{minRows: 5, maxRows: 20}">
      </el-string-autocomplete>
    </el-form-small-item>
    <el-form-sub
      v-else-if="body_type === 'form'"
      :prop="prop && prop+'.body'"
      :model="{body:form_body}">
      <el-form-errors v-for="(value, index) in form_body"
        :key="value.key">
        <el-button-group style="width: 100%">
          <el-form-small-item
            style="max-width: 200px"
            :prop="'body.'+index+'.name'"
            :required="!value.temporary"
            :disabled="disabled"
            :rules="fieldRules">
            <el-input
              v-model="value.name"
              placeholder="field name">
            </el-input>
          </el-form-small-item>
          <el-form-small-item
            :prop="'body.'+index+'.value'"
            :required="!value.temporary"
            :rules="StringVarValidator"
            :disabled="disabled">
            <el-string-autocomplete
              v-model="value.value"
              placeholder="field value"
              :local-variables="localVariables"
              :context-variables="contextVariables">
            </el-string-autocomplete>
          </el-form-small-item>
          <el-button
            :icon="value.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
            :disabled="disabled || value.temporary"
            @click="deleteFormField(value.key)"
            :tabindex="-1"></el-button>
        </el-button-group>
      </el-form-errors>
    </el-form-sub>
    <el-form-small-item v-else-if="body_type === 'json'"
      :prop="prop && prop+'.body'">
      <el-code-input
        type="multiline"
        :local-variables="localVariables"
        :context-variables="contextVariables"
        v-model="json_body"
        :autosize="{minRows: 5, maxRows: 20}">
      </el-code-input>
    </el-form-small-item>
  </orderable-box>
</template>

<script>
import deepEqual from 'fast-deep-equal'

import State from '../../stores/state.js'
import { VariableValidator, URLValidator, StringVarValidator, HTTPSValidator, urlWhitelistSummary, urlMatchWhitelist }
  from '../../utils/validators'
import OrderableBox from '../orderable/OrderableBox.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import ElStringAutocomplete from '../element/ElStringAutocomplete.vue'
import ElCodeInput from '../element/ElCodeInput.vue'
import ElEntityInput from '../element/ElEntityInput.vue'

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

const BODY_OPTIONS = [{
  label: 'Empty body',
  value: 'empty',
}, {
  label: 'String body',
  value: 'string',
  tooltip: 'The <em>body</em> is passed without any <em>Content-Type</em> header',
}, {
  label: 'Form body',
  value: 'form',
  tooltip: 'The <em>body</em> is url-encoded and passed with the heade<br/><em>Content-Type: application/x-www-form-urlencoded</em>',
}, {
  label: 'JSON body',
  value: 'json',
  tooltip: 'The <em>body</em> is JSON-encoded and passed with the header<br/><em>Content-Type: application/json</em>',
}]

export default {
  components: {
    OrderableBox,
    ElFilterableSelect,
    ElStringAutocomplete,
    ElCodeInput,
    ElEntityInput,
  },
  name: 'ApiActionEditor',
  props: {
    value: Object,
    skill: String,
    view: String,
    apiOptions: Array,
    localVariables: Array,
    contextVariables: Array,
    prop: String,
    required: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
  },
  data () {
    return {
      api: null,
      method: 'GET',
      url: '',
      body_type: 'empty',
      string_body: '',
      form_body: [{
        name: '',
        value: '',
        temporary: true,
      }],
      json_body: '',
      variable: 'response',
      METHOD_OPTIONS,
      BODY_OPTIONS,
      VariableValidator,
      StringVarValidator,
      fieldRules: [
        VariableValidator,
        {
          validator: (rule, value, callback) => callback(
            value.name && this.formFieldCount[value.name] > 1
              ? new Error('duplicate field') : undefined),
        },
      ],
      urlRules: [
        URLValidator,
        HTTPSValidator,
        {
          trigger: 'blur',
          validator: (rule, value, callback) => {
            this.apiPromise.then(({ data, whitelist }) => {
              if (!data) return callback()
              return urlMatchWhitelist(value, whitelist, data.whitelist && data.whitelist.length, callback)
            })
          },
        },
        StringVarValidator,
      ],
      apiPromise: Promise.resolve({}),
      apiView: null,
    }
  },
  watch: {
    defaultedValue: 'updateValue',
    computedValue: 'valueUpdated',
    form_body: {
      handler: 'updateFormBody',
      deep: true,
    },
    apiParams: 'getApi',
    apiView (data) {
      if (!this.url && data && data.whitelist && data.whitelist.length === 1) {
        this.url = data.whitelist[0]
      }
    },
  },
  // to display errors
  inject: ['onerror'],
  created () {
    this.updateValue()
  },
  computed: {
    computedValue () {
      let body = this.body_type === 'string' ? this.string_body
        : this.body_type === 'form' ? this.computedFormBody
          : this.body_type === 'json' ? this.json_body
            : null
      return {
        type: 'api',
        api: this.api,
        url: this.url,
        method: this.method,
        body_type: this.body_type,
        body: body,
        variable: this.variable,
      }
    },
    defaultedValue () {
      return {
        type: 'api',
        api: this.value.api || null,
        method: this.value.method || 'GET',
        url: this.value.url || '',
        body_type: this.value.body_type || 'empty',
        body: (this.value.body_type || 'empty') === 'empty'
          ? null : this.value.body || '',
        variable: this.value.variable,
      }
    },
    computedFormBody () {
      let body = {}
      for (let value of this.form_body) {
        if (!value.temporary && value.name) {
          body[value.name] = value.value
        }
      }
      return body
    },
    formFieldCount () {
      let count = {}
      this.form_body.forEach(value => {
        if (value.field && !value.temporary) {
          count[value.field] = (count[value.field] || 0) + 1
        }
      })
      return count
    },
    apiParams () {
      return [this.skill || '', this.api || '', this.view || ''].join('/')
    },
  },
  methods: {
    updateValue () {
      if (deepEqual(this.computedValue, this.defaultedValue)) {
        return
      }
      const value = this.defaultedValue
      this.api = value.api
      this.method = value.method
      this.url = value.url
      this.body_type = value.body_type
      this.variable = value.variable
      if (value.body_type === 'string') {
        this.string_body = value.body
      } else if (value.body_type === 'json') {
        this.json_body = value.body
      } else if (value.body_type === 'form') {
        let body = Object.entries(value.body).map(([k, v]) => ({
          key: Math.random() + '',
          name: k,
          value: v,
        }))
        body.push({
          key: Math.random() + '',
          name: '',
          value: '',
          temporary: true,
        })
        this.form_body = body
      }
    },
    valueUpdated () {
      if (!deepEqual(this.computedValue, this.defaultedValue)) {
        this.$emit('input', this.computedValue)
      }
    },
    updateFormBody () {
      let temporary = this.form_body.find(v => v.temporary)
      if (temporary && (temporary.name || temporary.value)) {
        temporary.temporary = false
        temporary = null
      }
      if (!temporary) {
        this.form_body.push({
          key: Math.random() + '',
          name: '',
          value: '',
          temporary: true,
        })
      }
    },
    deleteFormField (key) {
      this.form_body = this.form_body.filter(value => value.key !== key)
    },
    getApi () {
      this.apiView = null
      if (!this.api) {
        this.apiPromise = Promise.resolve({})
        return
      }
      const view = this.view
      this.apiPromise = State.getApi(this.skill, this.api, this.view)
        .then((data) => {
          this.apiView = data || null
          let whitelist = data ? urlWhitelistSummary(data.whitelist, data.blacklist) : []
          return { data, view, whitelist }
        })
        .catch(this.onerror)
    },
  },
}
</script>
